import { supabase } from '@/lib/supabase';
import type { Brand } from '@/shared/brand/brands';

/**
 * Google OAuth, shared by both brands.
 *
 * Google sees neither brand: one OAuth client, one consent screen
 * ("Numtable · Procalendar"), and the whole flow passes through Supabase's
 * callback. See docs/GOOGLE-OAUTH.md.
 *
 * The brand only decides *where the user lands afterwards*. The origin always
 * comes from `window.location`, never from `BRANDS[brand].homeUrl`: in dev both
 * brands run on 127.0.0.1:5176, and hardcoding the production host would bounce
 * every local login to numtable.com.
 */

/** Where each brand sends a user who just signed in and asked for nothing else. */
const DEFAULT_LANDING: Readonly<Record<Brand, string>> = {
  numtable: '/',
  procalendar: '/me/bookings',
};

export interface SignInWithGoogleOptions {
  /** Decides the default landing path. Read it from `useBrand()`, never hardcode. */
  readonly brand: Brand;
  /**
   * Path to land on after the callback, relative to the current origin.
   * Overrides the brand default. The resulting URL must match a pattern in
   * Supabase → Authentication → URL Configuration, or Supabase silently falls
   * back to the Site URL — which can only ever be one of the two brands.
   */
  readonly next?: string;
  /**
   * Force Google's account chooser instead of reusing the only signed-in
   * account. Worth it on shared phones, and on procalendar.app where a client
   * may have a personal and a work Google account.
   */
  readonly promptSelectAccount?: boolean;
}

export async function signInWithGoogle({
  brand,
  next,
  promptSelectAccount = false,
}: SignInWithGoogleOptions): Promise<never> {
  const redirectTo = new URL(
    next ?? DEFAULT_LANDING[brand],
    window.location.origin,
  ).toString();

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      // Scopes stay at the default openid/email/profile. Those are
      // non-sensitive, so Google asks for no verification review and shows no
      // "unverified app" warning. Requesting more changes that — deliberate
      // decision, not an oversight.
      ...(promptSelectAccount ? { queryParams: { prompt: 'select_account' } } : {}),
    },
  });

  if (error) throw error;

  // On success the browser is already navigating to Google, so control never
  // comes back here. Hanging keeps callers from rendering a post-success state
  // over a page that is about to be torn down.
  return new Promise<never>(() => {});
}
